import { useState, useEffect } from 'react';
import { AGENCY_DEFAULT_COMMISSION_FALLBACK } from '../services/settingsService';
import { Plus, CreditCard as Edit2, Trash2, X, CircleUser as UserCircle } from 'lucide-react';
import { influencerService } from '../services/influencerService';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import type { Influencer, InfluencerInsert } from '../types/influencer';
import { logSessionDiagnostics } from '../utils/diagnosticHelpers';
import { log } from '../lib/logger';

export function InfluencersManager() {
  const { user } = useAuth();
  const { t, translateError } = useLanguage();
  const { showConfirm, toast } = useModal();
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState<Influencer | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    id_number: '',
    address: '',
    iban: '',
    default_commission_pct: AGENCY_DEFAULT_COMMISSION_FALLBACK,
    default_commission_flat_fee: 0,
    default_commission_type: 'percentage' as 'percentage' | 'flat_fee',
  });

  useEffect(() => {
    loadInfluencers();
    // Log diagnostic information for debugging
    logSessionDiagnostics().catch(log.error);
  }, []);

  const loadInfluencers = async () => {
    try {
      setLoading(true);
      log.debug('Loading influencers...');
      const data = await influencerService.getAll();
      log.debug('Loaded influencers:', data.map(inf => ({
        id: inf.id,
        name: inf.full_name,
        default_commission_pct: inf.default_commission_pct,
      })));
      setInfluencers(data);
    } catch (error) {
      log.error('Error loading influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingInfluencer) {
        // Log the data being sent for debugging
        log.debug('Updating influencer:', {
          id: editingInfluencer.id,
          formData,
          default_commission_pct: formData.default_commission_pct,
        });

        const updatedInfluencer = await influencerService.update(editingInfluencer.id, formData);

        // Log the response
        log.debug('Update successful, response:', {
          id: updatedInfluencer.id,
          default_commission_pct: updatedInfluencer.default_commission_pct,
        });

        toast.success(t('influencers.updateSuccess'));
      } else {
        const influencerData: InfluencerInsert = {
          ...formData,
          user_id: user?.id,
        };
        log.debug('Creating influencer with data:', influencerData);
        await influencerService.create(influencerData);
        toast.success(t('influencers.createSuccess'));
      }
      await loadInfluencers();
      handleCloseForm();
    } catch (error: any) {
      log.error('Error saving influencer:', error);

      // Enhanced error logging for debugging
      if (error?.message) {
        log.error('Error message:', error.message);
      }
      if (error?.details) {
        log.error('Error details:', error.details);
      }
      if (error?.hint) {
        log.error('Error hint:', error.hint);
      }
      if (error?.code) {
        log.error('Error code:', error.code);
      }

      const errorMessage = translateError(error);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (influencer: Influencer) => {
    setEditingInfluencer(influencer);
    setFormData({
      full_name: influencer.full_name,
      company_name: influencer.company_name || '',
      email: influencer.email || '',
      id_number: influencer.id_number || '',
      address: influencer.address || '',
      iban: influencer.iban || '',
      default_commission_pct: influencer.default_commission_pct ?? AGENCY_DEFAULT_COMMISSION_FALLBACK,
      default_commission_flat_fee: influencer.default_commission_flat_fee ?? 0,
      default_commission_type: influencer.default_commission_type ?? 'percentage',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const influencer = influencers.find(i => i.id === id);
    const confirmed = await showConfirm({
      title: 'Delete Influencer?',
      message: `Are you sure you want to delete ${influencer?.full_name || 'this influencer'}? This action cannot be undone.`,
      confirmText: 'Delete Influencer',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await influencerService.delete(id);
      toast.success(t('influencers.deleteSuccess'));
      await loadInfluencers();
    } catch (error) {
      log.error('Error deleting influencer:', error);
      toast.error(t('influencers.deleteFailed'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingInfluencer(null);
    setFormData({ full_name: '', company_name: '', email: '', id_number: '', address: '', iban: '', default_commission_pct: AGENCY_DEFAULT_COMMISSION_FALLBACK, default_commission_flat_fee: 0, default_commission_type: 'percentage' });
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('influencers.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('influencers.description')}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {influencers.length} {influencers.length === 1 ? t('influencers.influencer') : t('influencers.influencers')}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100">
        <div className="p-6 border-b">
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('influencers.addNew')}
            </button>
          </div>
        </div>

      <div className="p-6">
        {loading ? (
          <p className="text-gray-500 text-center py-8">{t('influencers.loadingInfluencers')}</p>
        ) : influencers.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('influencers.noInfluencersYet')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {influencers.map((influencer) => (
              <div key={influencer.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{influencer.full_name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(influencer)}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(influencer.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {influencer.slug && (
                    <div className="mb-3 p-2 bg-purple-50 rounded border border-purple-200">
                      <span className="block font-medium text-purple-900 text-xs mb-1">Custom URL</span>
                      <code className="text-purple-700 text-xs break-all">
                        {window.location.origin}/influencer/{influencer.slug}
                      </code>
                    </div>
                  )}
                  {influencer.company_name && (
                    <div>
                      <span className="font-medium text-gray-600">{t('influencers.companyLabel')}</span>
                      <span className="ml-2 text-gray-900">{influencer.company_name}</span>
                    </div>
                  )}
                  {influencer.email && (
                    <div>
                      <span className="font-medium text-gray-600">{t('influencers.emailLabel')}</span>
                      <span className="ml-2 text-gray-900">{influencer.email}</span>
                    </div>
                  )}
                  {influencer.id_number && (
                    <div>
                      <span className="font-medium text-gray-600">{t('influencers.idLabel')}</span>
                      <span className="ml-2 text-gray-900">{influencer.id_number}</span>
                    </div>
                  )}
                  {influencer.address && (
                    <div>
                      <span className="font-medium text-gray-600">{t('influencers.addressLabel')}</span>
                      <p className="text-gray-900 mt-1">{influencer.address}</p>
                    </div>
                  )}
                  {influencer.iban && (
                    <div>
                      <span className="font-medium text-gray-600">{t('influencers.ibanLabel')}</span>
                      <p className="text-gray-900 mt-1 font-mono text-xs">{influencer.iban}</p>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-600">Default Commission</span>
                    <p className="text-gray-900 mt-1">
                      {influencer.default_commission_type === 'flat_fee'
                        ? new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(influencer.default_commission_flat_fee ?? 0)
                        : `${((influencer.default_commission_pct ?? 0) * 100).toFixed(0)}%`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingInfluencer ? t('influencers.editInfluencer') : t('influencers.addInfluencer')}
              </h2>
              <button onClick={handleCloseForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form id="influencer-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('influencers.fullName')} *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('influencers.fullNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('influencers.companyName')}</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('influencers.companyNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('influencers.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('influencers.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('influencers.idNumber')}</label>
                <input
                  type="text"
                  value={formData.id_number}
                  onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('influencers.idNumberPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  placeholder={t('influencers.addressPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('influencers.iban')}</label>
                <input
                  type="text"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 font-mono"
                  placeholder={t('influencers.ibanPlaceholder')}
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Default Commission Type
                </label>
                <div className="flex rounded-xl overflow-hidden border border-blue-300 mb-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, default_commission_type: 'percentage' })}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      formData.default_commission_type === 'percentage'
                        ? 'bg-green-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Percentage
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, default_commission_type: 'flat_fee' })}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      formData.default_commission_type === 'flat_fee'
                        ? 'bg-green-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Flat Fee
                  </button>
                </div>

                {formData.default_commission_type === 'percentage' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Default Commission (%)
                    </label>
                    <input
                      type="number"
                      value={Math.round(formData.default_commission_pct * 100)}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        setFormData({ ...formData, default_commission_pct: value / 100 });
                      }}
                      step="1"
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      placeholder="30"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Default Commission (EUR)
                    </label>
                    <input
                      type="number"
                      value={formData.default_commission_flat_fee || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        setFormData({ ...formData, default_commission_flat_fee: value });
                      }}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      placeholder="500"
                    />
                  </div>
                )}
                <p className="text-xs text-blue-700 mt-2 font-medium">
                  This commission will be automatically applied to new campaigns. Existing campaigns will not be affected.
                </p>
              </div>

            </form>

            <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={handleCloseForm}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="influencer-form"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (editingInfluencer ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
